import React from "react";
import { Loader2, CheckCircle, XCircle, FileText, Clock } from "lucide-react";

const ProcessingStep = ({ fileProgressMap, totalFiles }) => {
  const files = Object.values(fileProgressMap);
  const completedFiles = files.filter((f) => f.status === "completed" || f.status === "failed" || f.status === "skipped").length;
  const overallProgress = totalFiles > 0 ? (completedFiles / totalFiles) * 100 : 0;

  return (
    <div className="py-6 space-y-6 transition-colors duration-200">
      {/* Progress Summary */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full">
          <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Processing Documents
          </h3>
          <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
            {completedFiles} of {totalFiles} complete
          </p>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-slate-400 mb-2">
          <span>Progress</span>
          <span>{Math.round(overallProgress)}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
          <div
            className="bg-blue-600 dark:bg-blue-500 h-full rounded-full transition-all duration-300"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      {/* File List */}
      <div className="max-w-2xl mx-auto max-h-80 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
        {files.map((file) => (
          <div
            key={file.fileId}
            className={`border rounded-lg p-3 transition-all ${
              file.status === "completed"
                ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                : file.status === "failed"
                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                : file.status === "skipped"
                ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
                : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${
                file.status === "completed" ? "bg-green-100 dark:bg-green-900/40" :
                file.status === "failed" ? "bg-red-100 dark:bg-red-900/40" :
                file.status === "skipped" ? "bg-yellow-100 dark:bg-yellow-900/40" :
                "bg-gray-100 dark:bg-slate-700"
              }`}>
                {file.status === "completed" && <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />}
                {file.status === "failed" && <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />}
                {file.status === "skipped" && <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />}
                {file.status === "processing" && <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {file.fileName}
                </p>
                {file.status === "processing" && (
                  <div className="mt-1.5">
                    <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">{file.message}</p>
                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1">
                      <div
                        className="bg-blue-600 dark:bg-blue-500 h-full rounded-full transition-all duration-300"
                        style={{ width: `${file.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}
                {file.status === "completed" && (
                  <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                    {file.ticketsCount || 0} tickets extracted
                  </p>
                )}
                {file.status === "failed" && (
                  <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                    {file.error || "Failed"}
                  </p>
                )}
                {file.status === "skipped" && (
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                    {file.reason || "Skipped"}
                  </p>
                )}
              </div>

              {file.status === "processing" && (
                <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProcessingStep;
